import clsx from 'clsx';
import React from 'react';
import { AiFillCheckCircle } from 'react-icons/ai';
import { FaRegClock } from 'react-icons/fa';
import { IoCloseCircleSharp } from 'react-icons/io5';
import { MdCancel } from 'react-icons/md';
import { PiClockCountdownBold } from 'react-icons/pi';

import { Button } from '@/components/button/button';
import { FriendlyError } from '@/components/friendlyError/friendlyError';
import Modal from '@/components/modal/modal';
import { Heading } from '@/components/typography/heading';
import { ContractTaskStatus, TaskStatusResponse } from '@/types/dataContracts';

import styles from './TaskHistoryModal.module.scss';

interface TaskHistoryModalProps {
  taskStatus: TaskStatusResponse | undefined;
  isLoading: boolean;
  onClose: () => void;
}

export default function TaskHistoryModal( { taskStatus, isLoading, onClose }: TaskHistoryModalProps ) {
  const status = taskStatus?.status;
  const progress = taskStatus?.progress ?? 0;

  const renderStatusIcon = () => {
    switch ( status ) {
      case ContractTaskStatus.PENDING:
        return <PiClockCountdownBold className={ styles.iconQueued } />;
      case ContractTaskStatus.STARTED:
      case ContractTaskStatus.ACCESSCONTROL:
        return <FaRegClock className={ styles.iconProcessing } />;
      case ContractTaskStatus.SUCCESS:
        return <AiFillCheckCircle className={ styles.iconCompleted } />;
      case ContractTaskStatus.FAILURE:
      case ContractTaskStatus.REVOKED:
        return <IoCloseCircleSharp className={ styles.iconFailed } />;
      case ContractTaskStatus.RETRY:
        return <MdCancel className={ styles.iconQueued } />;
      default:
        return <PiClockCountdownBold className={ styles.iconQueued } />;
    }
  };

  const renderStatusText = () => {
    if ( isLoading && !taskStatus ) return 'Loading task details...';

    switch ( status ) {
      case ContractTaskStatus.PENDING:
        return 'Task was queued for processing.';
      case ContractTaskStatus.STARTED:
      case ContractTaskStatus.ACCESSCONTROL:
        return taskStatus?.progress_message || 'Task was in progress.';
      case ContractTaskStatus.SUCCESS:
        return 'Task completed successfully.';
      case ContractTaskStatus.FAILURE:
      case ContractTaskStatus.REVOKED:
        return 'Task failed.';
      case ContractTaskStatus.RETRY:
        return 'Task was retried.';
      default:
        return 'No task details available.';
    }
  };

  const progressBarClass = clsx(
    styles.progressBarFill,
    status === ContractTaskStatus.SUCCESS && styles.progressBarCompleted,
    ( status === ContractTaskStatus.FAILURE || status === ContractTaskStatus.REVOKED ) && styles.progressBarFailed
  );

  return (
    <Modal>
      <div className={ clsx( 'layout__col', 'pad', styles.modal ) }>
        <Heading as="h2" size="md">Last Processing Details</Heading>

        <div className={ styles.statusRow }>
          { renderStatusIcon() }
          <span className={ styles.statusText }>{ renderStatusText() }</span>
        </div>

        <div className={ styles.progressBarContainer }>
          <div className={ progressBarClass } style={ { width: `${ progress }%` } } />
        </div>
        <span className={ styles.progressText }>{ progress }% complete</span>

        { ( status === ContractTaskStatus.FAILURE || status === ContractTaskStatus.REVOKED ) && taskStatus?.error && (
          <div className={ styles.errorMessage }>
            <FriendlyError error={ taskStatus.error } />
          </div>
        ) }

        <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
          <Button variant='primary' onClick={ onClose }>Close</Button>
        </div>
      </div>
    </Modal>
  );
}
